import { ReactElement } from "react";
import Image, { ImageLoaderProps } from "next/image";
import {
    CmsMedia,
    CommonMediaAttributes,
    CmsMediaImageMimetype,
} from "@/types/strapi";
import {
    closestNumberInList,
    getAllMedias,
    getImageUrl,
} from "src/utils/utils";
import styles from "./CmsImage.module.css";

const checkLocalRunning = (src: string, width: number): string => {
    // add width as a fragment of the url as this prevents a warning appearing in the compiler
    //   - it doesn't actually do anything but that is ok, our earlier logic optimized the image
    // properly already.
    return getImageUrl(`${src}#${width}`);
};

const getMatchingImageUrl = (
    mediaAttributes: CommonMediaAttributes[],
    goalWidth: number
): string => {
    const matchingImage = getFirstMatchingImage(mediaAttributes, goalWidth);
    return checkLocalRunning(matchingImage?.url ?? "", goalWidth);
};

const getFirstMatchingImage = (
    mediaAttributes: CommonMediaAttributes[],
    goalWidth: number
): CommonMediaAttributes | null => {
    const webpImages = mediaAttributes.filter(
        (mediaAttribute: CommonMediaAttributes) =>
            mediaAttribute.ext === ".webp"
    );

    const selectedMediaAttributes =
        webpImages.length > 0 ? webpImages : mediaAttributes;

    const mediaAttributesWidthArray = selectedMediaAttributes.map(
        (mediaAttribute: CommonMediaAttributes): number => mediaAttribute.width
    );

    const imgWidth =
        mediaAttributesWidthArray.length > 0
            ? closestNumberInList(goalWidth, mediaAttributesWidthArray)
            : goalWidth;

    return (
        selectedMediaAttributes.find(
            (mediaAttribute: CommonMediaAttributes): boolean =>
                mediaAttribute.width === imgWidth
        ) ?? null
    );
};

export const getMatchingImageAttributes = (
    medias: CommonMediaAttributes[],
    goalMimetypes: CmsMediaImageMimetype | CmsMediaImageMimetype[],
    goalWidth?: number
): CommonMediaAttributes | null => {
    const filterMimetypes: CmsMediaImageMimetype[] = Array.isArray(
        goalMimetypes
    )
        ? goalMimetypes
        : [goalMimetypes];
    const mimetypeFilteredMedias = medias.filter(
        (media: CommonMediaAttributes) => filterMimetypes.includes(media.mime)
    );

    let foundMedia: CommonMediaAttributes | null = null;
    if (goalWidth && mimetypeFilteredMedias) {
        foundMedia = getFirstMatchingImage(mimetypeFilteredMedias, goalWidth);
    } else {
        foundMedia = mimetypeFilteredMedias[0] ?? null;
    }
    return foundMedia;
};

const cmsLoader = (
    { src, width }: ImageLoaderProps,
    cmsMedia: CmsMedia | null
): string => {
    if (cmsMedia?.data?.attributes?.mime === CmsMediaImageMimetype.SVG) {
        return checkLocalRunning(src, width);
    } else {
        return getMatchingImageUrl(getAllMedias(cmsMedia?.data ?? null), width);
    }
};

export const CmsImage = ({
    cmsMedia,
    desiredHeight,
    maxWidth,
    defaultAlt = "",
    responsive = false,
    loading = "lazy",
    priority = false,
    style = {},
}: {
    cmsMedia: CmsMedia | null;
    desiredHeight?: number;
    maxWidth?: number;
    defaultAlt?: string;
    responsive?: boolean;
    loading?: "eager" | "lazy";
    priority?: boolean;
    style?: React.CSSProperties;
}): ReactElement => {
    const mediaAttributes = cmsMedia?.data?.attributes;

    const actualWidth = mediaAttributes?.width || 1;
    const actualHeight = mediaAttributes?.height || 1;

    let calculatedWidth = actualWidth;
    let calculatedHeight = actualHeight;
    if (!responsive && desiredHeight) {
        // Calculate the aspect ratio so we can maintain it
        const aspectRatio = actualWidth / actualHeight;

        // Calculate the dimensions from the desired height
        calculatedWidth = desiredHeight * aspectRatio;
        calculatedHeight = desiredHeight;

        // If using the desired height results in a width larger than the maximum width
        // then recalculate using the dimensions using the max width instead
        if (maxWidth && calculatedWidth > maxWidth) {
            calculatedHeight = maxWidth / aspectRatio;
            calculatedWidth = maxWidth;
        }
    }

    return (
        <Image
            className={responsive ? styles.responsive : ""}
            width={calculatedWidth}
            height={calculatedHeight}
            priority={priority}
            alt={
                mediaAttributes?.alternativeText ||
                mediaAttributes?.captions ||
                defaultAlt
            }
            loading={loading}
            src={mediaAttributes?.url || ""}
            loader={(imgContext: ImageLoaderProps): string => {
                return cmsLoader(imgContext, cmsMedia);
            }}
            style={{
                maxWidth: "100%",
                height: "auto",
                ...style,
            }}
        />
    );
};
